import { Injectable, inject } from '@angular/core';
import { DashboardService } from '@pages/dashboard/dashboard.service';
import { ErrorHandlingService } from './error-handling.service';
import { TranslateService } from '@ngx-translate/core';
import { NafathStatusEnums } from '@common/enums/nafath.enum';
import { WathiqStatusEnums } from '@common/enums/wathiq.enum';
import { catchError, filter, Observable, of, switchMap, take } from 'rxjs';
import { InvestorProfile } from '@common/interfaces/investor-profile';
import { FirebaseRemoteConfigService } from './firebase-remote-config.service';

@Injectable({
  providedIn: 'root',
})
export class BankAccountService {
  dashboardService = inject(DashboardService);
  errorHandlingService = inject(ErrorHandlingService);
  translate = inject(TranslateService);
  // wathiq_rollback - Inject FirebaseRemoteConfigService for centralized wathiq flow status
  firebaseRemoteConfigService = inject(FirebaseRemoteConfigService);

  checkBankAccountNavigation(): Observable<boolean> {
    return this.dashboardService.investorProfile.pipe(
      filter(profile => profile !== undefined),
      take(1),
      switchMap(profile => {
        return of(this.checkVerificationStatus(profile as InvestorProfile));
      }),
      catchError(error => {
        this.errorHandlingService.showError(error);
        return of(false);
      })
    );
  }

  checkVerificationStatus(investorProfile: InvestorProfile | null): boolean {
    if (!investorProfile || !investorProfile.investor) {
      return false;
    }

    const investor = investorProfile.investor;

    // Check Nafath for type 1 (Individual)
    if (investor.type === 1) {
      if (
        investor.nafathRequestStatus === NafathStatusEnums.NOT_REQUIRED ||
        investor.nafathRequestStatus === NafathStatusEnums.COMPLETED
      ) {
        return true;
      } else {
        this.errorHandlingService.showError({
          message: this.translate.instant(
            'common_nafath_status_not_completed_error'
          ),
        });
        return false;
      }
    }

    // wathiq_rollback - Check Wathiq for type 2 (Corporate) only if flow is enabled (using centralized signal)
    if (
      this.firebaseRemoteConfigService.turn_on_watheeq_flow() &&
      investor.type === 2
    ) {
      const wathiqStatus = investor?.wathqStatus;

      // wathiq_rollback - Check wathiq status only if flow is enabled
      if (wathiqStatus === WathiqStatusEnums.COMPLETED) {
        return true;
      } else {
        this.errorHandlingService.showError({
          message: this.translate.instant(
            'common_wathiq_status_not_completed_error'
          ),
        });
        return false;
      }
    }

    // wathiq_rollback - If wathiq flow is disabled, allow navigation for corporate
    if (investor.type === 2) {
      return true;
    }

    return false;
  }
}
